import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {Place, PlaceResponse, UserPlace} from '../models/place';
import {LoaderService} from 'ngx-satoris';
import {DocumentType, User} from '../models/user';
import {Store} from '../models/store';
import {StorageService} from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class PlacesService {

  user: User;
  allPlaces: Place[];
  allPlacesByPerson: Place[];
  currentPlace: Place;
  userPlace: UserPlace;
  maxPlace = 0;

  constructor(private api: ApiService, private loader: LoaderService, private storage: StorageService) {}

  /**
   * Get all existing places
   */
  getAllPlaces(offset = 0, limit = 0, search = ''): Promise<PlaceResponse> {
    this.loader.loading(true);
    return this.api.searchPlace(search, offset, limit).then((res: any) => {
      this.allPlaces = res.result;
      this.maxPlace = res.count;
      this.loader.loading(false);
      return res;
    });
  }

  /**
   * Allow to get one place based on her id
   * @param id {string}
   */
  getPlace(id: string, loader = false, setAsCurrent = false, maxAge = 60000): Promise<Place> {
    if(!id) return Promise.resolve(null);
    if(loader) this.loader.loading(true);

    return this.storage.getFromStorage(Store.PLACE_STORE, id, false, true).then((res: { item: Place; lastModified: number }) => {
      const now = Date.now();

      // If the place is found in the storage and the last modification is less than maxAge, we return the place
      if(res) {
        if(setAsCurrent) this.currentPlace = res.item;

        // If the place is found in the storage but the last modification is older than maxAge, we update the place
        if(now - res.lastModified > maxAge || maxAge === 0) {
          this.api.place(id).then((place: Place) => {
            this.storage.saveToStorage(Store.PLACE_STORE, place);
          }).catch((error: any) => {
            console.error('API place update error:', error);
          });
        }

        if(loader) this.loader.loading(false);
        return res.item;

      } else {
        // If the place is not found in the storage, we get it from the API and save it in the storage
        return this.api.place(id).then((place: Place) => {
          this.storage.saveToStorage(Store.PLACE_STORE, place);
          if(loader) this.loader.loading(false);
          if(setAsCurrent) this.currentPlace = place;
          return place;
        }).catch((error: any) => {
          if(loader) this.loader.loading(false);
          throw error;
        });
      }
    });
  }


  /**
   * Get all places by the person (id)
   * @param id {number}
   */
  getAllPlacesByPerson(user: User): Promise<Place[]>{
    this.loader.loading(true);
    if(user.links.length > 0) {
      return this.api.listPlaces(user.links?.map((l:any) => l.place_id)).then((res) => {
        this.loader.loading(false);
        this.allPlacesByPerson = res;
        return res;
      }).catch((error: any) => {
        this.loader.loading(false);
        throw error;
      });
    } else {
      this.loader.loading(false);
      this.allPlacesByPerson = [];
      return Promise.resolve([]);
    }
  }

  invalidatePlaceCache(id: string): Promise<void> {
    localStorage.removeItem('places' + id);
    return this.storage.removeFromStorage(Store.PLACE_STORE, id);
  }

  /**
   * Get the place with her permissions
   * @param id {number} id of the user
   * @param placeId {string} id of the desired place
   * @param save
   */
  getPermissionPlace(id: number, placeId: string): Promise<void>{
    return this.api.user(id).then((res: User) => {
      this.user = res;
      return this.getPlace(placeId, false, false, 0).then(() => {
        this.userPlace = this.user.links.find((place: any) => place.place_id === placeId);
      });
    });
  }

  /**
   * Get the long name of a place from its ID
   * @param longName {string} long name of the place
   */
  getLongNameFromId(id: string): string {
    return this.api.userInfo.places?.find(place => place.id === id)?.longName || id;
  }

  getPlacesThatCanConsume(document: DocumentType, map: 'id'|'longName' = 'longName'): string[] {
    const doc = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    const places = this.api.userInfo.places;
    return doc.onlyIn?.map(placeId => places?.find(place => place.id === placeId)?.[map]).filter(x => x) || [];
  }
}
